import React, {useContext, useEffect, useState, useMemo, useCallback, memo} from 'react';
import { View, StyleSheet, TouchableOpacity, Text, Platform, Alert } from 'react-native';
import Icon from 'react-native-vector-icons/FontAwesome';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import TableMapScreen from "./TableMapScreen";
import OrdersScreen from "./OrdersScreen";
import TablesBookingsScreen from "./TablesBookingsScreen";
import FunctionsScreen from "./FunctionsScreen";
import { useTranslation } from 'react-i18next';
import '../../i18n';
import AsyncStorage from "@react-native-async-storage/async-storage";
import CircularButton from "../components/CircularButton";
import Tooltip from 'react-native-walkthrough-tooltip';
import { TourContext } from "../helpers/TourContext";
import CustomTooltip from "../helpers/CustomTooltip";
import { OrdersContext } from "../helpers/OrdersContext";
import IonIcons from "react-native-vector-icons/Ionicons";
import { getAccessToken } from "../helpers/storage";
const Tab = createBottomTabNavigator();

export default function TablesScreen({ navigation }) {
    const { t } = useTranslation();
    const [restaurantPlace, setRestaurantPlace] = useState({ _id: '' });
    const { currentStep, nextStep, endTour } = useContext(TourContext);
    const { orders, fetchOrdersFromAPI } = useContext(OrdersContext);
    const [pendingOrders, setPendingOrders] = useState([]);

    // Function to update order status
    const updateOrderStatus = async (orderId = "", status) => {
        try {
            const token = await getAccessToken();
            const response = await fetch(`https://api.happygastro.hu/api/order/${orderId}/status/${status}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error('Failed to update order status');
            }
            await response.json();
            await fetchOrdersFromAPI();
        } catch (err) {
            console.log("Error while updating order status:", err);
        }
    };

    useEffect(() => {
        const fetchRestaurantPlace = async () => {
            const restaurantPlaceStored = await AsyncStorage.getItem("restaurantPlace");
            if (restaurantPlaceStored) {
                setRestaurantPlace(JSON.parse(restaurantPlaceStored));
            }
        };
        fetchRestaurantPlace();
    }, []);

    // Update pendingOrders when orders change
    useEffect(() => {
        if (orders) {
            setPendingOrders(orders.filter(order => (order.status === "pending" && !order.storno)) || []);
        }
    }, [orders]);

    // Memoize the screen options to prevent unnecessary re-renders
    const screenOptions = useMemo(() => ({
        headerShown: false,
        // Use newer screenOptions API instead of deprecated tabBarOptions
        tabBarActiveTintColor: '#a26909',
        tabBarInactiveTintColor: '#575757',
        tabBarActiveBackgroundColor: '#f0f0f0',
        tabBarInactiveBackgroundColor: '#ffffff',
        tabBarShowLabel: true,
        tabBarLabelStyle: {
            fontSize: 14,
            fontWeight: 'bold',
        },
        tabBarStyle: {
            height: 70,
            backgroundColor: '#e0e0e0',
            paddingBottom: Platform.OS === 'ios' ? 20 : 0,
        },
        tabBarItemStyle: {
            paddingVertical: 10,
            height: 70,
        },
        // Improve animation performance
        animationEnabled: false,
    }), []);

    // Memoize tab icons to prevent recreation on each render
    const brailleIcon = useCallback(({ color, size }) => (
        <Icon name="braille" color={color} size={size} />
    ), []);

    const cutleryIcon = useCallback(({ color, size }) => (
        <Icon name="cutlery" color={color} size={size} />
    ), []);

    const calendarIcon = useCallback(({ color, size }) => (
        <Icon name="calendar" color={color} size={size} />
    ), []);

    const serverIcon = useCallback(({ color, size }) => (
        <CustomTooltip
            isVisible={false}
            onNext={() => {
                nextStep();
                navigation.navigate('Functions');
            }}
            placement="top"
            module={"table"}
            onClose={endTour}
            wrapped={(
                <Icon name="server" color={color} size={size} />
            )}
        />
    ), [nextStep, navigation, endTour]);

    return (
        <View style={{ flex: 1, width: '100%', height: '100%', flexGrow: 1 }}>
            {/* Bell icon with badge */}
            <View style={{ position: 'absolute', top: 10, right: 10, zIndex: 30 }}>
                <View style={[{
                    minWidth: 50,
                    minHeight: 50,
                    borderRadius: 25,
                    marginTop: 5,
                    marginRight: 10,
                    justifyContent: 'center',
                    alignItems: 'center'
                }, pendingOrders.length > 0 ? {backgroundColor: "#F1B44C"} : {backgroundColor: "#fde1b6"}]}>
                    <TouchableOpacity
                        activeOpacity={0.6}
                        onPress={() => navigation.navigate('NotificationsScreen')}
                        onLongPress={() => {
                            if (pendingOrders.length > 0) {
                                // Auto accept all pending orders
                                pendingOrders.forEach(order => {
                                    updateOrderStatus(order._id, 'accepted');
                                });

                                // Show alert notification
                                Alert.alert("Auto-Accept", `Successfully accepted ${pendingOrders.length} pending orders`);
                            }
                        }}
                    >
                        <View style={{ position: 'relative', justifyContent: 'center', alignItems: 'center' }}>
                            <IonIcons name="notifications" size={34} style={{color: '#6e6e6e',}} />
                            {pendingOrders.length > 0 && (
                                <View
                                    style={{
                                        position: 'absolute',
                                        top: -4,
                                        right: -2,
                                        backgroundColor: 'red',
                                        borderRadius: 10,
                                        width: 20,
                                        height: 20,
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Text style={{
                                        color: 'white',
                                        fontSize: 12,
                                        fontWeight: 'bold'
                                    }}>
                                        {pendingOrders.length}
                                    </Text>
                                </View>
                            )}
                        </View>
                    </TouchableOpacity>
                </View>
            </View>

            <Tab.Navigator
                detachInactiveScreens={true} // Changed to true for better performance
                tabBarLabelPosition={"below-icon"}
                lazy={true} // Changed to true for better initial load performance
                backBehavior={"history"}
                screenOptions={screenOptions}
            >
                <Tab.Screen
                    name="TableMap"
                    component={TableMapScreen}
                    options={{
                        tabBarIcon: brailleIcon,
                        title: t('table_map'),
                        tabBarLabel: t('table_map'),
                    }}
                />
                <Tab.Screen
                    name="OpenTables"
                    component={OrdersScreen}
                    options={{
                        tabBarIcon: cutleryIcon,
                        title: t('open_tables'),
                        tabBarLabel: t('open_tables'),
                    }}
                />
                <Tab.Screen
                    name="Bookings"
                    component={TablesBookingsScreen}
                    options={{
                        tabBarIcon: calendarIcon,
                        title: t('upcoming_bookings'),
                        tabBarLabel: t('upcoming_bookings')
                    }}
                />
                <Tab.Screen
                    name="Functions"
                    component={FunctionsScreen}
                    options={{
                        tabBarIcon: serverIcon,
                        title: t('functions_operations'),
                        tabBarLabel: t('functions_operations'),
                    }}
                />
            </Tab.Navigator>

            <CircularButton navigation={navigation} iconName="grid" />
        </View>
    );
}

const styles = StyleSheet.create({
    background: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
});
